import {
  CreditCard,
  MessageCircle,
  PenLine,
  Users,
  Wrench,
  type LucideIcon,
} from "@/components/icons";
import { isTruthyEnv } from "@/lib/maintenance";

type AppEnv = "production" | "staging" | "dev";

function cleanEnvValue(value: string | undefined): string | undefined {
  return value?.trim().replace(/^["']|["']$/g, "");
}

function resolveAppEnv(value: string | undefined): AppEnv {
  const env = cleanEnvValue(value)?.toLowerCase();
  if (env === "production" || env === "staging" || env === "dev") return env;
  return "production";
}

export const APP_ENV = resolveAppEnv(process.env.NEXT_PUBLIC_APP_ENV);

export const SALES_USE_CASES = [
  "Web scraping / Data collection",
  "Ad verification",
  "Social media monitoring",
  "Market research",
  "Building a proxy network",
  "Other",
] as const;

export const SALES_BENEFITS = [
  "Request a demo",
  "Learn which plan is right for your team",
  "Get onboarding help",
];

export type SupportCategory = { value: string; label: string; icon: LucideIcon };

export const SUPPORT_CATEGORIES: SupportCategory[] = [
  { value: "technical_issue", label: "Technical Issue", icon: Wrench },
  { value: "billing_and_payment", label: "Billing and Payment", icon: CreditCard },
  { value: "account_management", label: "Account & Subscription", icon: Users },
  { value: "general_inquiry1", label: "General Inquiry", icon: MessageCircle },
  { value: "FEATURE_REQUEST", label: "Feature Request", icon: PenLine },
];

export const SUPPORT_CATEGORY_SUBJECTS: Record<string, string> = {
  technical_issue: "🔧 Technical Issue",
  billing_and_payment: "💳 Billing and Payment",
  account_management: "👤 Account & Subscription",
  general_inquiry1: "❓ General Inquiry",
  FEATURE_REQUEST: "✏️ Feature Request",
};

export const REPORT_ABUSE_TYPES = [
  { value: "spam", label: "Spam" },
  { value: "ddos", label: "DDoS or network attack" },
  { value: "dmca", label: "Copyright or trademark infringement (DMCA)" },
  { value: "phishing", label: "Phishing or fraud" },
  { value: "account_security", label: "Account security incident" },
  { value: "csam", label: "Child sexual abuse material (CSAM) or exploitation of minors" },
  { value: "sanctions", label: "Sanctions or other illegal activity concern" },
  { value: "ip_sourcing", label: "IP sourcing concern" },
  { value: "other", label: "Other" },
] as const;

export type ReportAbuseType = (typeof REPORT_ABUSE_TYPES)[number]["value"];

export const BASE_URL = "https://proxidize.com";
const APP_URL_BY_ENV: Record<AppEnv, string> = {
  production: "https://app.proxidize.com/",
  staging: "https://www.app-staging.proxidize.com/",
  dev: "https://www.app-staging.proxidize.com/",
};
export const APP_URL = cleanEnvValue(process.env.NEXT_PUBLIC_APP_URL) ?? APP_URL_BY_ENV[APP_ENV];

const REGISTER_URL_BY_ENV: Record<AppEnv, string> = {
  production: "https://app.proxidize.com/register",
  staging: "https://www.app-staging.proxidize.com/register",
  dev: "https://www.app-staging.proxidize.com/register",
};
export const REGISTER_URL =
  cleanEnvValue(process.env.NEXT_PUBLIC_REGISTER_URL) ?? REGISTER_URL_BY_ENV[APP_ENV];

const FREE_TRIAL_URL_BY_ENV: Record<AppEnv, string> = {
  production: "https://app.proxidize.com/register",
  staging: "https://www.app-staging.proxidize.com/register",
  dev: "https://www.app-staging.proxidize.com/register",
};

export const FREE_TRIAL_URL =
  cleanEnvValue(process.env.NEXT_PUBLIC_FREE_TRIAL_URL) ?? FREE_TRIAL_URL_BY_ENV[APP_ENV];
export const FREE_TRIAL_LABEL = "Sign Up for Free";
export const G2_URL = "https://www.g2.com/products/proxidize/reviews";
export const TRUSTPILOT_URL = "https://www.trustpilot.com/review/proxidize.com";
export const HELP_URL = "https://help.proxidize.com/";
export const COMMUNITY_URL = "https://community.proxidize.com/";
// The docs site moved from developers.proxidize.com to help.proxidize.com with a
// restructured path scheme (e.g. "get-started" → "getting-started", "products" →
// "proxidize-proxies"). Point straight at the new domain/paths instead of relying
// on the old domain's redirects, some of which have no mapping and 404.
export const DEVELOPERS_URL = "https://help.proxidize.com/";
export const DOCS = {
  quickStart: `${DEVELOPERS_URL}getting-started/quick-start-with-proxidize-proxies`,
  chooseProxyType: `${DEVELOPERS_URL}getting-started/choose-between-mobile-and-residential-proxies`,
  mobileProxies: `${DEVELOPERS_URL}proxidize-proxies/mobile-proxies`,
  residentialProxies: `${DEVELOPERS_URL}proxidize-proxies/residential-pergb`,
  perProxyPlan: `${DEVELOPERS_URL}proxidize-proxies/mobile-proxies/mobile-per-proxy`,
  mobilePerGbPlan: `${DEVELOPERS_URL}proxidize-proxies/mobile-proxies/mobile-pergb`,
  apiOverview: `${DEVELOPERS_URL}proxidize-rest-api/proxidize-api`,
  apiReference: `${DEVELOPERS_URL}proxidize-rest-api/proxidize-api`,
  apiPerProxyMobile: `${DEVELOPERS_URL}proxidize-rest-api/proxidize-api/mobile-perproxy-api`,
  apiPerGbMobile: `${DEVELOPERS_URL}proxidize-rest-api/proxidize-api/mobile-pergb-api`,
  apiPerGbResidential: `${DEVELOPERS_URL}proxidize-rest-api/proxidize-api/residential-pergb-api`,
  // No dedicated troubleshooting hub exists for the current cloud product (only
  // legacy Proxy Builder hardware docs have one) — link to the closest live page.
  troubleshooting: `${DEVELOPERS_URL}proxidize-proxies/mobile-proxies`,
  proxyExtension: `${DEVELOPERS_URL}proxidize-proxies/proxidize-proxy-extension`,
  proxyExtensionWhenToUse: `${DEVELOPERS_URL}proxidize-proxies/proxidize-proxy-extension#when-to-use-the-extension`,
  mcpServer: `${DEVELOPERS_URL}proxidize-proxies/proxidize-mcp-server`,
} as const;
export const PLAY_STORE_URL = "https://play.google.com/store/search?q=proxidize&c=apps&hl=en";
export const SPEEDTEST_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=org.zwanoo.android.speedtest";
export const SPEEDTEST_APP_STORE_URL =
  "https://apps.apple.com/us/app/speedtest-by-ookla/id300704847";
export const AFFILIATE_URL = "https://proxidize.com/referral-program/";
export const CAREERS_URL = "https://www.careers-page.com/proxidize";
export const LINKEDIN_JOBS_URL = "https://www.linkedin.com/company/proxidize/jobs/";
export const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/proxidize-proxy-extension/gccdbcipcjgfnjlegofdhflkifgkdmpb?hl=en";
export const FIREFOX_EXTENSION_URL =
  "https://addons.mozilla.org/en-US/firefox/addon/proxidize-proxy-extension/";
export const TRUST_CENTER_URL = "https://trust.proxidize.com/";
export const STATUS_URL = "https://status.proxidize.com";
export const BOOK_DEMO_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ1dq20b6T2n2d7aimnXy4qO8W0Up81DU3-nipcr0Hjxnj_ySOtzo_zwLUU6notDmYrulTF7nkjM";
export const CONTACT_SALES_URL = "/contact/sales";
export const GITHUB_URL = "https://github.com/proxidize";
export const GITHUB_REDDIT_SCRAPER_URL = "https://github.com/proxidize/reddit-scraper";
export const GITHUB_X_SCRAPER_URL = "https://github.com/proxidize/x-scraper";
export const GITHUB_MCP_URL = "https://github.com/proxidize/proxidize-mcp";
export const GITHUB_FIRECRAWL_URL = "https://github.com/proxidize/firecrawl-proxidize";
export const NPM_MCP_URL = "https://www.npmjs.com/package/@proxidize/mcp";
export const MCP_SERVERS_DIRECTORY_URL = "https://mcpservers.org/servers/proxidize/proxidize-mcp";
export const MCP_INSTALL_CMD = "npx -y @proxidize/mcp";
export const LINUX_INSTALL_CMD = "curl -fsSL pxfile.com/install_cloud | sudo bash";

export const PATH_BACK_LABELS: Record<string, string> = {
  "/": "Back to Home",
  "/blogs": "Back to Blog",
  "/blog": "Back to Blog",
  "/research": "Back to Research",
  "/case-studies": "Back to Case Studies",
};

export const ROUTES = {
  signup: "/signup",
  productMobileProxies: "/proxy-server/mobile-proxies",
  productResidentialProxies: "/proxy-server/residential-proxies",
  deMobileProxies: "/de/mobile-proxies",
  deResidentialProxies: "/de/residential-proxies",
  pricingMobileProxies: "/mobile-proxy-pricing",
  pricingResidentialProxies: "/residential-proxies-pricing",
  blog: "/blog",
  writeForUs: "/write-for-us",
  referralProgram: "/referral-program",
  useCases: "/use-cases",
  contactSupport: "/contact/support",
  alternatives: "/alternatives",
  tools: "/tools",
  pricing: "/pricing/",
  locations: "/locations",
  locationsCountry: (country: string) => `/locations/${country}`,
  locationsCity: (country: string, city: string) => `/locations/${country}/${city}`,
  kyc: "/kyc",
} as const;

export const MAINTENANCE_MODE =
  isTruthyEnv(process.env.MAINTENANCE_MODE) ||
  isTruthyEnv(process.env.NEXT_PUBLIC_MAINTENANCE_MODE);

export const BRAND = "#F7F7F8";
export const BRAND_BG = "#0A0A0B";
export const BRAND_OVERLAY = "rgba(255, 255, 255, 0.04)";
/* Secondary accent — the hero ribbon's orange. Legacy export name kept: it has
   held yellow, then cyan, and now orange. Mirrors --yellow-400 in globals.css;
   keep the two in step. Blue (GREEN, below) stays the primary. */
export const YELLOW = "#ff8a5b";
export const GREEN = "#5b8cff";
export const G2_COLOR = "#FF492C";
export const TRUSTPILOT_COLOR = "#00B67A";
export const STAR_COLOR = "#f59e0b";

export const STATS = {
  ips: "Millions of",
  countries: "195+",
  uptime: "99.9%",
  customers: "thousands",
} as const;

export const EMAIL = {
  support: "support@proxidize.com",
  hello: "hello@proxidize.com",
  abuse: "abuse@proxidize.com",
  dmca: "dmca@proxidize.com",
  sales: "sales@proxidize.com",
  privacy: "privacy@proxidize.com",
  accessibility: "accessibility@proxidize.com",
  noreply: "noreply@proxidize.com",
  hosting: "hosting@proxidize.com",
  partners: "marketing@proxidize.com",
} as const;

export const logos = [
  { name: "Truv", abbr: "Truv", tint: "rgba(91,140,255,1)" },
  { name: "Lead Hype", abbr: "Lead Hype", tint: "rgba(255,160,60,1)" },
  { name: "Vebo", abbr: "Vebo", tint: "rgba(34,211,238,1)" },
  { name: "Sahara AI", abbr: "Sahara AI", tint: "rgba(60,220,180,1)" },
  { name: "Atlantech", abbr: "Atlantech", tint: "rgba(180,230,80,1)" },
  { name: "Mass Conversions", abbr: "Mass Conversions", tint: "rgba(34,211,238,1)" },
  { name: "Lumora", abbr: "Lumora", tint: "rgba(91,140,255,1)" },
  { name: "Stacked", abbr: "Stacked", tint: "rgba(255,160,60,1)" },
  { name: "Growth Radius", abbr: "Growth Radius", tint: "rgba(60,220,180,1)" },
  { name: "Distill", abbr: "Distill", tint: "rgba(180,230,80,1)" },
  { name: "iTupt", abbr: "iTupt", tint: "rgba(34,211,238,1)" },
  { name: "Kazeel", abbr: "Kazeel", tint: "rgba(91,140,255,1)" },
  { name: "Adseas Ventures", abbr: "Adseas Ventures", tint: "rgba(255,160,60,1)" },
  { name: "Forage Data", abbr: "Forage Data", tint: "rgba(60,220,180,1)" },
  { name: "Sales Hub", abbr: "Sales Hub", tint: "rgba(34,211,238,1)" },
  { name: "Blckpanda", abbr: "Blckpanda", tint: "rgba(180,230,80,1)" },
  { name: "Ametist", abbr: "Ametist", tint: "rgba(91,140,255,1)" },
  { name: "Hyper Dog", abbr: "Hyper Dog", tint: "rgba(255,160,60,1)" },
  { name: "LeadX", abbr: "LeadX", tint: "rgba(60,220,180,1)" },
  { name: "Surge Digital", abbr: "Surge Digital", tint: "rgba(34,211,238,1)" },
];

export const logos2 = [
  { name: "News Desk", abbr: "News Desk", tint: "rgba(180,230,80,1)" },
  { name: "Soverin AI", abbr: "Soverin AI", tint: "rgba(91,140,255,1)" },
  { name: "Adfluential", abbr: "Adfluential", tint: "rgba(255,160,60,1)" },
  { name: "Payperlead.com", abbr: "Payperlead.com", tint: "rgba(34,211,238,1)" },
  { name: "Matrix Mobile", abbr: "Matrix Mobile", tint: "rgba(60,220,180,1)" },
  { name: "Belvo", abbr: "Belvo", tint: "rgba(180,230,80,1)" },
  { name: "All Gas SEO", abbr: "All Gas SEO", tint: "rgba(91,140,255,1)" },
  { name: "Evrim", abbr: "Evrim", tint: "rgba(255,160,60,1)" },
  { name: "Activechase", abbr: "Activechase", tint: "rgba(34,211,238,1)" },
  { name: "IPV Data", abbr: "IPV Data", tint: "rgba(60,220,180,1)" },
  { name: "Case Engine", abbr: "Case Engine", tint: "rgba(180,230,80,1)" },
  { name: "Fluxify", abbr: "Fluxify", tint: "rgba(91,140,255,1)" },
  { name: "Traffic Revenue", abbr: "Traffic Revenue", tint: "rgba(255,160,60,1)" },
  { name: "IP Lasso", abbr: "IP Lasso", tint: "rgba(34,211,238,1)" },
  { name: "Retailogy", abbr: "Retailogy", tint: "rgba(60,220,180,1)" },
  { name: "Appy Furious", abbr: "Appy Furious", tint: "rgba(180,230,80,1)" },
  { name: "Nuvoretail", abbr: "Nuvoretail", tint: "rgba(91,140,255,1)" },
  { name: "Zelf", abbr: "Zelf", tint: "rgba(255,160,60,1)" },
  { name: "OMG Rentals", abbr: "OMG Rentals", tint: "rgba(34,211,238,1)" },
  { name: "Arsolabs", abbr: "Arsolabs", tint: "rgba(60,220,180,1)" },
];

export const platformScores = [
  {
    platform: "G2",
    score: "4.7",
    max: "5.0",
    stars: 5,
    href: G2_URL,
  },
  {
    platform: "Trustpilot",
    score: "4.8",
    max: "5.0",
    stars: 5,
    href: TRUSTPILOT_URL,
  },
];

export const PROXY_LOCATIONS = [
  { id: "ny", flag: "🇺🇸", city: "New York", ip: "104.28.14.71", ms: 12 },
  { id: "ld", flag: "🇬🇧", city: "London", ip: "185.71.12.44", ms: 28 },
  { id: "am", flag: "🇳🇱", city: "Amsterdam", ip: "185.234.8.17", ms: 19 },
  { id: "pa", flag: "🇫🇷", city: "Paris", ip: "192.168.52.9", ms: 31 },
  { id: "be", flag: "🇩🇪", city: "Berlin", ip: "91.108.4.188", ms: 34 },
  { id: "to", flag: "🇨🇦", city: "Toronto", ip: "198.51.18.3", ms: 21 },
  { id: "sg", flag: "🇸🇬", city: "Singapore", ip: "167.88.61.22", ms: 52 },
  { id: "tk", flag: "🇯🇵", city: "Tokyo", ip: "139.180.7.14", ms: 89 },
  { id: "sy", flag: "🇦🇺", city: "Sydney", ip: "103.87.24.55", ms: 167 },
  { id: "du", flag: "🇦🇪", city: "Dubai", ip: "94.141.22.37", ms: 112 },
  { id: "mu", flag: "🇮🇳", city: "Mumbai", ip: "103.55.14.88", ms: 78 },
  { id: "mx", flag: "🇲🇽", city: "Mex. City", ip: "187.190.4.14", ms: 55 },
] as const;

export type ProxyLocation = (typeof PROXY_LOCATIONS)[number];

// `product` tags which proxy line a review explicitly praises (mentions mobile IPs/carriers,
// a mobile-specific price, etc.) so product-specific pages (e.g. residential) can exclude
// testimonials that are actually about the other product line.
export const reviews = [
  {
    id: 1,
    platform: "G2",
    rating: 5,
    author: "Bryant A.",
    product: "general" as const,
    quote:
      "The UI is really easy to understand, even if you're not familiar with tech or proxy terminology. I'm a complete beginner with all of this, and I understood it right away. Their support chat is also world-class: I've never waited more than a few minutes, and they actually understand what you're asking. It was straightforward to integrate my proxy. Performance-wise it's extremely fast — I have friends paying 5x the price for less performance.",
  },
  {
    id: 2,
    platform: "G2",
    rating: 5,
    author: "Amr K.",
    product: "mobile" as const,
    quote:
      "I was thoroughly impressed with how straightforward the setup process was. The proxy worked smoothly right from the start without any complicated configuration. The connection speeds were highly stable throughout my usage, and the IP quality was excellent. It delivers exactly what you expect from a premium mobile proxy provider.",
  },
  {
    id: 3,
    platform: "G2",
    rating: 5,
    author: "Simon F.",
    product: "general" as const,
    quote: "The customer service is amazing, I had some issues and they worked hard to fix them.",
  },
  {
    id: 4,
    platform: "G2",
    rating: 5,
    author: "Joye K.",
    product: "mobile" as const,
    quote:
      "The best parts about Proxidize are that the proxies come from real USA mobile ISPs and use dynamic IPs that can be refreshed with a single click or through the rotating API. As mobile IPs, they are quite fast. The customer support via live chat is straightforward and extremely helpful. They support both HTTP and SOCKS5 protocols, and unlike many other providers, they offer unlimited mobile data with no bandwidth limits.",
  },
  {
    id: 5,
    platform: "G2",
    rating: 5,
    author: "Marcos P.",
    product: "mobile" as const,
    quote: "High quality US mobile proxy with an easy purchase option.",
  },
  {
    id: 6,
    platform: "G2",
    rating: 5,
    author: "Limon P.",
    product: "mobile" as const,
    quote:
      "Proxidize is the most robust proxy solution I have ever used. From their UI to their proxy qualities, they do an incredible job. The support is very quick and helpful. Fresh IPs, mobile IPs, proxy rotation API, and city-level targeting are the best features. It's also very easy to integrate with browsers like Dolphin Anty or Gologin.",
  },
  {
    id: 7,
    platform: "G2",
    rating: 5,
    author: "yuk man l.",
    product: "mobile" as const,
    quote:
      "Proxidize has quickly become my go-to choice for mobile proxies. At just $59/month, the pricing is incredibly competitive — especially considering how feature-packed the service is. Setting up Proxidize is effortless: switching between SOCKS5 or HTTP protocols takes seconds. Their live support team is responsive and knowledgeable. The service offers unlimited IP switching and the ability to target over 50 US cities.",
  },
  {
    id: 8,
    platform: "G2",
    rating: 5,
    author: "John S.",
    product: "general" as const,
    quote:
      "Ease of use. Intuitive user interface. Fantastic customer support. Competitive pricing per proxy.",
  },
  {
    id: 9,
    platform: "G2",
    rating: 5,
    author: "chux i.",
    product: "general" as const,
    quote: "Very easy to configure, great value for money, and very reliable.",
  },
  {
    id: 10,
    platform: "G2",
    rating: 5,
    author: "Tahmid B.",
    product: "general" as const,
    quote:
      "Proxidize is a good proxy provider on the internet. Their proxies are fresh and moderately speedy. The UI is the most beloved thing about their service.",
  },
];

/**
 * Attribution required by the CC-BY-4.0 licence on the DB-IP Lite databases the tools read
 * locally for IP network data. Render it wherever that data is displayed.
 *
 * The wording is DB-IP's own prescribed snippet, not a paraphrase — their licensing page asks
 * for exactly `<a href='https://db-ip.com'>IP Geolocation by DB-IP</a>` on pages that display
 * results from the database. Do not reword it to fit a layout.
 *
 * Lives here rather than beside the lookup code because that module is server-only (it reads
 * from disk), so importing it into a Client Component pulls node:fs into the browser bundle.
 */
export const GEO_DATA_ATTRIBUTION = {
  text: "IP Geolocation by DB-IP",
  href: "https://db-ip.com",
  /** CC-BY-4.0 asks for credit AND a link to the licence. DB-IP's prescribed snippet only covers
   * the credit, so the licence link is carried separately to satisfy both. */
  licenseText: "CC BY 4.0",
  licenseHref: "https://creativecommons.org/licenses/by/4.0/",
} as const;
